import { FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { api, apiError } from "../api/client";
import { IgAuthMethod, InstagramAccount } from "../api/types";
import { TIMEZONE_GROUPS } from "../lib/timezones";
import { PROXY_COUNTRIES } from "../lib/proxyCountries";
import { useProxyInfo, type ProxyInfo } from "../hooks/useProxyInfo";
import AuthMethodHelp from "../components/AuthMethodHelp";

type Step = "credentials" | "auth_method" | "connecting" | "done" | "error";

export default function AddInstagramAccount() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>("credentials");

  const proxyInfoQuery = useProxyInfo();
  const proxyInfo = proxyInfoQuery.data;

  const [igUsername, setIgUsername] = useState("");
  const [password, setPassword] = useState("");
  const [proxyUrl, setProxyUrl] = useState("");
  const [proxyCountry, setProxyCountry] = useState("");
  const [showAdvancedProxy, setShowAdvancedProxy] = useState(false);
  const [timezone, setTimezone] = useState(
    typeof Intl !== "undefined"
      ? Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"
      : "UTC"
  );

  const [authMethod, setAuthMethod] = useState<IgAuthMethod>("interactive");
  const [totpSecret, setTotpSecret] = useState("");
  const [backupCodes, setBackupCodes] = useState("");

  const [accountId, setAccountId] = useState<string | null>(null);
  const [account, setAccount] = useState<InstagramAccount | null>(null);
  const [pollingError, setPollingError] = useState<string | null>(null);

  const [twoFaCode, setTwoFaCode] = useState("");
  const [submittingCode, setSubmittingCode] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Poll the account while step === "connecting".
  useEffect(() => {
    if (step !== "connecting" || !accountId) return;

    let cancelled = false;
    let attempts = 0;
    // ~5 minutes at 2s intervals. The backend may need to rotate the proxy IP
    // up to PROXY_MAX_ROTATIONS times if Instagram throws a reCAPTCHA wall;
    // each rotation reopens Chromium + reloads the login form, which adds ~30s.
    const maxAttempts = 150;

    async function tick() {
      if (cancelled) return;
      attempts++;
      try {
        const { data } = await api.get<{ accounts: InstagramAccount[] }>(
          "/api/instagram-accounts"
        );
        const found = data.accounts.find((a) => a.id === accountId);
        if (!found) {
          setPollingError(t("addAccount.error.notFound"));
          setStep("error");
          return;
        }
        setAccount(found);

        if (found.status === "active") {
          setStep("done");
          return;
        }
        if (found.status === "error" || found.status === "challenge_required") {
          setStep("error");
          return;
        }
        // pending_login or awaiting_2fa_code -> keep polling
        if (attempts >= maxAttempts) {
          setPollingError(t("addAccount.error.timedOut"));
          setStep("error");
          return;
        }
        setTimeout(tick, 2000);
      } catch (err) {
        setPollingError(apiError(err));
        setStep("error");
      }
    }

    tick();
    return () => {
      cancelled = true;
    };
  }, [step, accountId, t]);

  async function submitCredentials(e: FormEvent) {
    e.preventDefault();
    setStep("auth_method");
  }

  async function submitAccount() {
    setSubmitting(true);
    try {
      const body: Record<string, unknown> = {
        igUsername: igUsername.trim().toLowerCase(),
        password,
        proxyUrl: proxyUrl.trim() || undefined,
        proxyCountry: proxyCountry || undefined,
        timezone,
        authMethod,
      };
      if (authMethod === "totp") body.totpSecret = totpSecret.trim();
      if (authMethod === "backup_codes") body.backupCodes = backupCodes;

      const { data } = await api.post<{ account: InstagramAccount }>(
        "/api/instagram-accounts",
        body
      );
      setAccountId(data.account.id);
      setAccount(data.account);
      setStep("connecting");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function submit2faCode() {
    if (!accountId) return;
    setSubmittingCode(true);
    try {
      await api.post(`/api/instagram-accounts/${accountId}/submit-2fa-code`, {
        code: twoFaCode.trim(),
      });
      setTwoFaCode("");
      toast.success(t("addAccount.toast.codeSubmitted"));
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSubmittingCode(false);
    }
  }

  return (
    <div className="mx-auto max-w-2xl">
      <h1 className="mb-1 text-2xl font-semibold">{t("addAccount.title")}</h1>
      <p className="mb-6 text-sm text-slate-400">{t("addAccount.intro")}</p>

      <Stepper step={step} />

      {step === "credentials" && (
        <form onSubmit={submitCredentials} className="card space-y-4">
          <div>
            <label className="label">{t("addAccount.credentials.username")}</label>
            <input
              type="text"
              required
              autoFocus
              className="input"
              placeholder={t("addAccount.credentials.usernamePlaceholder")}
              value={igUsername}
              onChange={(e) => setIgUsername(e.target.value)}
            />
          </div>
          <div>
            <label className="label">{t("addAccount.credentials.password")}</label>
            <input
              type="password"
              required
              className="input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
            />
          </div>
          <div>
            <label className="label">{t("addAccount.credentials.timezone")}</label>
            <select
              className="input"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
            >
              {TIMEZONE_GROUPS.map((group) => (
                <optgroup key={group.region} label={group.region}>
                  {group.zones.map((tz) => (
                    <option key={tz.id} value={tz.id}>
                      {tz.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
          <ProxySection
            proxyInfo={proxyInfo}
            timezone={timezone}
            proxyCountry={proxyCountry}
            setProxyCountry={setProxyCountry}
            proxyUrl={proxyUrl}
            setProxyUrl={setProxyUrl}
            showAdvancedProxy={showAdvancedProxy}
            setShowAdvancedProxy={setShowAdvancedProxy}
          />

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => navigate("/instagram-accounts")}
            >
              {t("common.cancel")}
            </button>
            <button type="submit" className="btn-primary">
              {t("common.continue")}
            </button>
          </div>
        </form>
      )}

      {step === "auth_method" && (
        <div className="card space-y-5">
          <div>
            <h3 className="text-base font-medium">
              {t("addAccount.authMethod.heading")}
            </h3>
            <p className="mt-1 text-sm text-slate-400">
              {t("addAccount.authMethod.help")}
            </p>
          </div>

          <AuthOption
            current={authMethod}
            value="interactive"
            title={t("addAccount.authMethod.interactive.title")}
            description={t("addAccount.authMethod.interactive.description")}
            onChange={setAuthMethod}
            help={<AuthMethodHelp method="interactive" />}
          />
          <AuthOption
            current={authMethod}
            value="backup_codes"
            title={t("addAccount.authMethod.backupCodes.title")}
            description={t("addAccount.authMethod.backupCodes.description")}
            onChange={setAuthMethod}
            help={<AuthMethodHelp method="backup_codes" />}
          >
            <textarea
              className="input mt-2 font-mono"
              rows={4}
              placeholder={t("addAccount.authMethod.backupCodes.placeholder")}
              value={backupCodes}
              onChange={(e) => setBackupCodes(e.target.value)}
            />
            <p className="mt-1 text-xs text-slate-500">
              {t("addAccount.authMethod.backupCodes.hint")}
            </p>
          </AuthOption>
          <AuthOption
            current={authMethod}
            value="totp"
            title={t("addAccount.authMethod.totp.title")}
            description={t("addAccount.authMethod.totp.description")}
            onChange={setAuthMethod}
            help={<AuthMethodHelp method="totp" />}
          >
            <input
              type="text"
              className="input mt-2 font-mono"
              placeholder={t("addAccount.authMethod.totp.placeholder")}
              value={totpSecret}
              onChange={(e) => setTotpSecret(e.target.value)}
            />
          </AuthOption>

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setStep("credentials")}
            >
              {t("common.back")}
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={submitting}
              onClick={submitAccount}
            >
              {submitting
                ? t("addAccount.authMethod.saving")
                : t("addAccount.authMethod.save")}
            </button>
          </div>
        </div>
      )}

      {step === "connecting" && (
        <div className="card space-y-4">
          <div className="flex items-center gap-3">
            <Spinner />
            <div>
              <h3 className="text-base font-medium">
                {account?.status === "awaiting_2fa_code"
                  ? t("addAccount.connecting.awaitingCodeHeading")
                  : t("addAccount.connecting.heading")}
              </h3>
              <p className="text-sm text-slate-400">
                {account?.status === "awaiting_2fa_code"
                  ? t("addAccount.connecting.awaitingCodeSubtitle")
                  : t("addAccount.connecting.subtitle")}
              </p>
            </div>
          </div>

          {account?.status === "awaiting_2fa_code" && (
            <div className="space-y-3">
              <div className="flex flex-col gap-2 sm:flex-row">
                <input
                  type="text"
                  inputMode="numeric"
                  autoFocus
                  pattern="[0-9 ]*"
                  maxLength={20}
                  className="input font-mono tracking-widest"
                  placeholder={t("addAccount.connecting.codePlaceholder")}
                  value={twoFaCode}
                  onChange={(e) => setTwoFaCode(e.target.value)}
                />
                <button
                  type="button"
                  className="btn-primary"
                  disabled={twoFaCode.trim().length < 4 || submittingCode}
                  onClick={submit2faCode}
                >
                  {submittingCode
                    ? t("addAccount.connecting.submitting")
                    : t("addAccount.connecting.submit")}
                </button>
              </div>
              {account?.statusReason && (
                <p className="text-xs text-rose-300">{account.statusReason}</p>
              )}
            </div>
          )}
        </div>
      )}

      {step === "done" && (
        <div className="card space-y-3 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-300">
            <CheckIcon />
          </div>
          <h3 className="text-base font-medium">
            {t("addAccount.done.heading", { username: account?.igUsername || "" })}
          </h3>
          <p className="text-sm text-slate-400">{t("addAccount.done.subtitle")}</p>
          <div className="flex flex-col-reverse justify-center gap-2 sm:flex-row">
            <button
              className="btn-secondary"
              onClick={() => navigate("/instagram-accounts")}
            >
              {t("addAccount.done.seeAccounts")}
            </button>
            <button className="btn-primary" onClick={() => navigate("/campaigns")}>
              {t("addAccount.done.createCampaign")}
            </button>
          </div>
        </div>
      )}

      {step === "error" && (
        <div className="card space-y-3">
          <h3 className="text-base font-medium text-rose-300">
            {t("addAccount.error.heading")}
          </h3>
          <p className="text-sm text-slate-400">
            {pollingError || account?.statusReason || t("addAccount.error.fallback")}
          </p>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              className="btn-secondary"
              onClick={() => navigate("/instagram-accounts")}
            >
              {t("addAccount.error.back")}
            </button>
            <button
              className="btn-primary"
              onClick={() => {
                setAccount(null);
                setAccountId(null);
                setPollingError(null);
                setStep("credentials");
              }}
            >
              {t("addAccount.error.startOver")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

interface AuthOptionProps {
  current: IgAuthMethod;
  value: IgAuthMethod;
  title: string;
  description: string;
  onChange: (v: IgAuthMethod) => void;
  /** Always rendered (e.g. step-by-step help). */
  help?: React.ReactNode;
  /** Only rendered when this option is selected (e.g. an input). */
  children?: React.ReactNode;
}

function AuthOption({
  current,
  value,
  title,
  description,
  onChange,
  help,
  children,
}: AuthOptionProps) {
  const selected = current === value;
  return (
    <label
      className={clsx(
        "block cursor-pointer rounded-lg border p-4 transition",
        selected
          ? "border-brand-400 bg-brand-500/5"
          : "border-slate-800 bg-slate-950 hover:border-slate-700"
      )}
    >
      <div className="flex items-start gap-3">
        <input
          type="radio"
          className="mt-1"
          checked={selected}
          onChange={() => onChange(value)}
        />
        <div className="flex-1">
          <div className="text-sm font-medium">{title}</div>
          <div className="mt-0.5 text-xs text-slate-400">{description}</div>
        </div>
      </div>
      {help}
      {selected && children}
    </label>
  );
}

function Stepper({ step }: { step: Step }) {
  const { t } = useTranslation();
  const order: Exclude<Step, "error">[] = [
    "credentials",
    "auth_method",
    "connecting",
    "done",
  ];
  const current = order.indexOf(step === "error" ? "connecting" : step);
  return (
    <div className="mb-4 flex items-center gap-2 text-xs text-slate-400">
      {order.map((label, i) => (
        <div key={label} className="flex items-center gap-2">
          <span
            className={clsx(
              "flex h-6 w-6 items-center justify-center rounded-full border text-xs",
              i < current
                ? "border-emerald-500 bg-emerald-500/15 text-emerald-300"
                : i === current
                ? "border-brand-400 bg-brand-500/15 text-brand-200"
                : "border-slate-700 text-slate-500"
            )}
          >
            {i + 1}
          </span>
          <span className={clsx(i === current && "text-slate-200")}>
            {t(`addAccount.steps.${label}`)}
          </span>
          {i < order.length - 1 && <span className="text-slate-700">›</span>}
        </div>
      ))}
    </div>
  );
}

function Spinner() {
  return (
    <svg
      className="h-6 w-6 animate-spin text-brand-300"
      viewBox="0 0 24 24"
      fill="none"
    >
      <circle
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="3"
        opacity="0.25"
      />
      <path
        d="M22 12a10 10 0 0 1-10 10"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      className="h-6 w-6"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

interface ProxySectionProps {
  proxyInfo: ProxyInfo | undefined;
  timezone: string;
  proxyCountry: string;
  setProxyCountry: (v: string) => void;
  proxyUrl: string;
  setProxyUrl: (v: string) => void;
  showAdvancedProxy: boolean;
  setShowAdvancedProxy: (v: boolean) => void;
}

/**
 * Renders the proxy block in the create-account form.
 *  - When the platform has a managed proxy configured (the common case), we
 *    show a green "managed" banner + an optional geo dropdown.
 *  - The "Use my own proxy URL" disclosure is hidden behind a toggle so the
 *    average musician never sees it. Power users still get the override.
 */
function ProxySection({
  proxyInfo,
  proxyCountry,
  setProxyCountry,
  proxyUrl,
  setProxyUrl,
  showAdvancedProxy,
  setShowAdvancedProxy,
}: ProxySectionProps) {
  const { t } = useTranslation();
  const managedEnabled = Boolean(proxyInfo?.enabled);
  const overrideActive = proxyUrl.trim().length > 0;

  return (
    <div className="space-y-2">
      <label className="label">{t("addAccount.credentials.proxy")}</label>

      {managedEnabled && !overrideActive && (
        <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-xs text-emerald-100">
          <div className="flex items-start gap-2">
            <ShieldIcon />
            <div>
              <div className="font-medium">{t("addAccount.proxy.managed.title")}</div>
              <p className="mt-0.5 text-emerald-200/80">
                {t("addAccount.proxy.managed.body")}
              </p>
            </div>
          </div>
        </div>
      )}

      {!managedEnabled && !overrideActive && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-100">
          {t("addAccount.proxy.disabledNotice")}
        </div>
      )}

      {managedEnabled && !overrideActive && (
        <div className="grid gap-2 sm:grid-cols-[1fr_auto] sm:items-end">
          <div>
            <label className="label text-xs text-slate-400">
              {t("addAccount.proxy.countryLabel")}
            </label>
            <select
              className="input"
              value={proxyCountry}
              onChange={(e) => setProxyCountry(e.target.value)}
            >
              <option value="">
                {t("addAccount.proxy.countryAuto", {
                  defaultCountry: (proxyInfo?.defaultCountry || "us").toUpperCase(),
                })}
              </option>
              {PROXY_COUNTRIES.filter((c) =>
                proxyInfo?.supportedCountries.includes(c.code)
              ).map((c) => (
                <option key={c.code} value={c.code}>
                  {t(`proxy.countries.${c.i18nKey}`, { defaultValue: c.fallback })}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      <button
        type="button"
        className="text-xs text-slate-400 underline-offset-2 hover:text-slate-200 hover:underline"
        onClick={() => setShowAdvancedProxy(!showAdvancedProxy)}
      >
        {showAdvancedProxy
          ? t("addAccount.proxy.hideAdvanced")
          : t("addAccount.proxy.showAdvanced")}
      </button>

      {showAdvancedProxy && (
        <div className="rounded-lg border border-slate-800 bg-slate-950 p-3">
          <label className="label text-xs text-slate-400">
            {t("addAccount.proxy.customUrlLabel")}
          </label>
          <input
            type="text"
            className="input"
            placeholder={t("addAccount.credentials.proxyPlaceholder")}
            value={proxyUrl}
            onChange={(e) => setProxyUrl(e.target.value)}
          />
          <p className="mt-1 text-xs text-slate-500">
            {t("addAccount.credentials.proxyHint")}
          </p>
        </div>
      )}
    </div>
  );
}

function ShieldIcon() {
  return (
    <svg
      className="mt-0.5 h-4 w-4 shrink-0 text-emerald-300"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <polyline points="9 12 11 14 15 10" />
    </svg>
  );
}
